const Backbone = require('backbone');
import * as _ from 'underscore';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import { ATTRIBUTE_SEPARATOR } from '../../../../pdc-platform/src/ts/stores/EntityModelsStore';
import CustomStore from 'devextreme/data/custom_store';

import { IStoreInsertParameters, IStoreRemoveParameters, IStoreUpdateParameters, IDataEditValues } from './Interfaces';
import { IAttributeName } from '../filtering/Interfaces';
import { IAttributeReference } from '../../ts/AqlUtility';
import { getNewID } from '../../ts/Utilities';

const ON_CHANGE_EVENT = 'change';
const ON_COMPLETE_OPERATIONS_EVENT = 'complete-operations';
const ON_CANCEL_OPERATIONS_EVENT = 'cancel-operations';

export type OperationType = 'insert' | 'update' | 'delete';

export interface IAttrQueryTransactionOperation {
    type: OperationType;
    dataSourceID: number;
    entity: string;
    newAttrValues?: {
        attributes: string[];
        values: any[];

        refAttributes?: string[];
        refValues?: any[];
    };
    virtualId?: number;
}

export interface IRequestResultOperation extends IAttrQueryTransactionOperation {
    oldAttrValues?: {
        attributes: string[];
        values: any[];
    };
    returnAttributes?: IAttributeReference[];
}

export interface ITransactionRequest {
    request: IRequestResultOperation;
    _tempKey?: string;
}

export interface IRequestData {
    entity: string;
    newAttributes?: string[];
    newValues?: any[];
    oldAttributes?: string[];
    oldValues?: any[];
    returnAttributes?: IAttributeName[];
    tmpKeyValue?: any;
}

export interface IOperation {
    type: OperationType;
    dataSourceID: number;
    keyAttributes: string[];
    keyValue: any;
    oldValues?: IDataEditValues;
    values?: IDataEditValues;
    store?: CustomStore;
    defferedResult?: JQueryDeferred<any>;
    onRefresh?: () => void;

    data?: IRequestData[];
    tmpKeyValues?: { [keyAttribute: string]: any };
}

var __: any = _;

enum OperationAddReturn {
    APPLY_STORE_AND_SET,
    APPLY_STORE_NOT_SET,
    DO_NOTHING
}

const TRANSACTION_DEFERRED = 'transaction-deferred';
const OPERATIONS = 'operations';
const OPERATIONS_MAP = 'operationsMap';

export function createTemporaryKey() {
    return '<temporary>-' + getNewID();
}

export function isTemporaryKey(value) {
    return typeof value === 'string' && /^<temporary>\-\d+$/.test(value);
}

interface IOperationMap {
    [key: string]: IOperation;
}

export class TransactionModel extends Backbone.Model {
    constructor(private glob: IGlob) {
        super({
            operations: [],
            operationsMap: {},
            transactionInProgress: false
        });
    }

    public getOperations() {
        return this.get(OPERATIONS) as IOperation[];
    }

    public getOperationsMap() {
        return this.get(OPERATIONS_MAP) as IOperationMap;
    }

    // Key of a row is a value of Key Attribute. The value can be the same as a Key Attribute's value of another Entity.
    private getOperationKey(operation: IOperation) {
        return operation.dataSourceID + ':' + operation.keyAttributes.join(':') + ':' + operation.keyValue;
    }

    private getSettingsObject(operations: IOperation[], operationsMap: IOperationMap) {
        const result: any = {};

        result[OPERATIONS] = operations;
        result[OPERATIONS_MAP] = operationsMap;

        return result;
    }

    private addOperationUpdate(
        newOperation: IOperation,
        existingOperation: IOperation,
        existingOperationIndex: number
    ) {
        const operations = [...this.getOperations()];
        const operationsMap = { ...this.getOperationsMap() };

        let result: OperationAddReturn;

        if (existingOperation != null && existingOperation.type === 'update') {
            newOperation.oldValues = _.extend(newOperation.oldValues, existingOperation.oldValues);
            newOperation.values = _.extend(existingOperation.values, newOperation.values);

            if (!_.isEqual(newOperation.values, newOperation.oldValues)) {
                operations.splice(existingOperationIndex, 1, newOperation);
            } else {
                const operationKey = this.getOperationKey(newOperation);

                delete operationsMap[operationKey];
                operations.splice(existingOperationIndex, 1);

                result = OperationAddReturn.APPLY_STORE_NOT_SET;
            }
        } else if (existingOperation != null && existingOperation.type === 'insert') {
            existingOperation.values = _.extend(existingOperation.values, newOperation.values);

            result = OperationAddReturn.APPLY_STORE_NOT_SET;
        } else if (existingOperation != null) {
            this.clearOperationInStore(existingOperation, false);
            operations.splice(existingOperationIndex, 1, newOperation);
        } else {
            operations.push(newOperation);
        }

        if (result == null) {
            result = OperationAddReturn.APPLY_STORE_AND_SET;
        }

        this.set(this.getSettingsObject(operations, operationsMap));

        return result;
    }

    private addOperationRemove(
        newOperation: IOperation,
        existingOperation: IOperation,
        existingOperationIndex: number
    ) {
        const operations = [...this.getOperations()];

        let returnValue: OperationAddReturn;

        if (existingOperation != null && existingOperation.type === 'insert') {
            this.clearOperationInStore(existingOperation, false);
            operations.splice(existingOperationIndex, 1);

            returnValue = OperationAddReturn.APPLY_STORE_NOT_SET;
        } else if (existingOperation != null) {
            this.clearOperationInStore(existingOperation, false);
            operations.splice(existingOperationIndex, 1, newOperation);
        } else {
            operations.push(newOperation);
        }

        if (returnValue == null) {
            returnValue = OperationAddReturn.APPLY_STORE_AND_SET;
        }

        this.set(OPERATIONS, operations);

        return OperationAddReturn.APPLY_STORE_AND_SET;
    }

    private addOperationInsert(newOperation: IOperation) {
        const operations = [...this.getOperations()];

        operations.push(newOperation);
        this.set(OPERATIONS, operations);

        return OperationAddReturn.APPLY_STORE_AND_SET;
    }

    public addOperation(newOperation: IOperation) {
        const operations = this.getOperations();
        const operationsMap = { ...this.getOperationsMap() };
        const operationKey = this.getOperationKey(newOperation);
        const existingOperation = operationsMap[operationKey];
        const existingOperationIndex = operations.indexOf(existingOperation);

        let res: OperationAddReturn;

        switch (newOperation.type) {
            case 'update':
                res = this.addOperationUpdate(newOperation, existingOperation, existingOperationIndex);
                break;

            case 'delete':
                res = this.addOperationRemove(newOperation, existingOperation, existingOperationIndex);
                break;

            case 'insert':
                res = this.addOperationInsert(newOperation);
                break;
        }

        switch (res) {
            case OperationAddReturn.APPLY_STORE_AND_SET:
                operationsMap[operationKey] = newOperation;
                this.set(OPERATIONS_MAP, operationsMap);

                return true;

            case OperationAddReturn.APPLY_STORE_NOT_SET:
                return true;

            case OperationAddReturn.DO_NOTHING:
            default:
        }

        return false;
    }

    public removeOperation(predicate: (operation: IOperation) => boolean) {
        const operationsMap = { ...this.getOperationsMap() };

        for (let key in operationsMap) {
            const operation = operationsMap[key];

            if (predicate(operation)) {
                const operations = [...this.getOperations()];
                const existingOperationIndex = operations.indexOf(operation);

                delete operationsMap[key];
                operations.splice(existingOperationIndex, 1);

                this.set(this.getSettingsObject(operations, operationsMap));
                break;
            }
        }
    }

    private callOnRefresh(operation: IOperation) {
        if (operation.onRefresh != null) {
            operation.onRefresh();
        }
    }

    private clearOperationInStore(operation: IOperation, refresh = true): JQuery.Promise<any> {
        if (operation.store == null) {
            // if (refresh) {
            //     this.callOnRefresh(operation);
            // }

            return $.Deferred().resolve().promise();
        }

        const deffered = $.Deferred();

        switch (operation.type) {
            case 'update':
                const storeUpdateParameters: IStoreUpdateParameters = {
                    newValues: operation.oldValues
                };

                operation.store
                    .update(operation.keyValue, storeUpdateParameters)
                    .then(() => {
                        deffered.resolve();
                    })
                    .catch(() => {
                        deffered.reject();
                    });
                break;

            case 'insert':
                const params: IStoreRemoveParameters = {
                    keyValue: operation.keyValue
                };

                operation.store
                    .remove(params)
                    .then(() => {
                        deffered.resolve();
                    })
                    .catch(() => {
                        deffered.reject();
                    });
                break;

            case 'delete':
                const storeRemoveParameters: IStoreRemoveParameters = {
                    keyValue: operation.keyValue,
                    rollback: true
                };

                operation.store
                    .remove(storeRemoveParameters)
                    .then(() => {
                        deffered.resolve();
                    })
                    .catch(() => {
                        deffered.reject();
                    });
                break;

            default:
                deffered.resolve();
        }

        return deffered.then(() => {
            if (refresh) {
                this.callOnRefresh(operation);
            }
        });
    }

    private clearCollections(promiseKey: string, inProgress?: boolean) {
        const result = this.getSettingsObject([], {});

        if (inProgress === false) {
            result[promiseKey] = null;
        }

        this.set(result);
    }

    public cancelOperations(runTimeWidgetID: number) {
        const promiseKey = TRANSACTION_DEFERRED + '-' + runTimeWidgetID;

        this.cancelOperationsByPromiseKey(promiseKey, true);
    }

    private cancelOperationsByPromiseKey(promiseKey: string, triggerCancel: boolean) {
        const operations = this.getOperations();
        const promises = _.map(operations, (operation) => {
            return this.clearOperationInStore(operation);
        });

        $.when.apply($, promises).always(() => {
            this.clearCollections(promiseKey, false);

            if (triggerCancel) {
                this.trigger(ON_CANCEL_OPERATIONS_EVENT);
            }
        });
    }

    private getShortAttributeName(attributeName: string) {
        const attributeParts = attributeName.split(ATTRIBUTE_SEPARATOR);

        if (attributeParts.length > 1) {
            return attributeParts[1];
        }

        return attributeName;
    }

    private createReturnAttributes(data: IRequestData) {
        const returnAttributes: IAttributeReference[] = [];
        const returnAttributeNames: IAttributeName[] = [];

        _.each(data.returnAttributes, (attribute) => {
            if (attribute.expression != null) {
                returnAttributes.push({
                    syntaxTree: attribute.expression.syntaxTree
                });
            } else {
                returnAttributes.push({
                    attrib: attribute.attribute.split(ATTRIBUTE_SEPARATOR)
                });
            }

            returnAttributeNames.push(attribute);
        });

        return {
            returnAttributes,
            returnAttributeNames
        };
    }

    public runTransaction(runTimeWidgetID: number, triggerCancelOnError = true): JQuery.Promise<any> {
        const promiseKey = TRANSACTION_DEFERRED + '-' + runTimeWidgetID;

        let result: JQuery.Deferred<any> = this.get(promiseKey);

        if (result != null) {
            return result.promise();
        }

        result = $.Deferred();

        const operations = this.getOperations();

        if (operations.length === 0) {
            return result.resolve().promise();
        }

        const requests: ITransactionRequest[] = [];
        const requestDefs: JQueryDeferred<any>[] = [];
        const onRefreshHandlers: { (): void }[] = [];

        _.each(operations, (operation) => {
            const dataSourceID = operation.dataSourceID;

            switch (operation.type) {
                case 'insert':
                    const storeInsertPromises: Promise<any>[] = [];
                    const keyValues: any[] = [];

                    _.each(operation.data, (data) => {
                        const def = operation.defferedResult || $.Deferred();
                        const newAttributes = _.map(data.newAttributes, (attributeName) => {
                            return this.getShortAttributeName(attributeName);
                        });

                        const { returnAttributes, returnAttributeNames } = this.createReturnAttributes(data);

                        requests.push({
                            request: {
                                type: 'insert',
                                entity: data.entity,
                                dataSourceID: dataSourceID,
                                newAttrValues: {
                                    attributes: newAttributes,
                                    values: data.newValues
                                },
                                returnAttributes: returnAttributes
                            },
                            _tempKey: data.tmpKeyValue
                        });

                        if (operation.store != null) {
                            const storeInsertParameters: IStoreInsertParameters = {
                                values: operation.values,
                                keyValue: operation.keyValue,
                                tmpKeyValues: operation.tmpKeyValues,
                                returnAttributes: returnAttributeNames,
                                keyAttributes: operation.keyAttributes,
                                promise: def.promise()
                            };
                            const storeInsertPromise = operation.store.insert(storeInsertParameters);

                            storeInsertPromises.push(storeInsertPromise);

                            if (keyValues.indexOf(operation.keyValue) < 0) {
                                keyValues.push(operation.keyValue);
                            }
                        } else if (operation.onRefresh != null) {
                            onRefreshHandlers.push(operation.onRefresh);
                        }

                        requestDefs.push(def);
                    });

                    if (storeInsertPromises.length > 0) {
                        Promise.all(storeInsertPromises).then(() => {
                            if (operation.store != null) {
                                _.each(keyValues, (keyValue) => {
                                    const storeRemoveParameters: IStoreRemoveParameters = {
                                        keyValue: keyValue,
                                        updateTemporaryRow: true
                                    };

                                    operation.store.remove(storeRemoveParameters);
                                });
                            }

                            this.callOnRefresh(operation);
                        });
                    }
                    break;

                case 'update':
                    const storeUpdatePromises: Promise<any>[] = [];

                    _.each(operation.data, (data) => {
                        const def = operation.defferedResult || $.Deferred();
                        const oldAttributes = _.map(data.oldAttributes, (attributeName) => {
                            return this.getShortAttributeName(attributeName);
                        });

                        const newAttributes = _.map(data.newAttributes, (attributeName) => {
                            return this.getShortAttributeName(attributeName);
                        });

                        const { returnAttributes, returnAttributeNames } = this.createReturnAttributes(data);

                        requests.push({
                            request: {
                                type: 'update',
                                entity: data.entity,
                                dataSourceID: dataSourceID,
                                oldAttrValues: {
                                    attributes: oldAttributes,
                                    values: data.oldValues
                                },
                                newAttrValues: {
                                    attributes: newAttributes,
                                    values: data.newValues
                                },
                                returnAttributes: returnAttributes
                            }
                        });

                        if (operation.store != null) {
                            const storeUpdateParameters: IStoreUpdateParameters = {
                                newValues: data.newValues,
                                oldValues: data.oldValues,
                                returnAttributes: returnAttributeNames,
                                promise: def.promise()
                            };
                            const storeUpdatePromise = operation.store.update(
                                operation.keyValue,
                                storeUpdateParameters
                            );

                            storeUpdatePromises.push(storeUpdatePromise);
                        } else if (operation.onRefresh != null) {
                            onRefreshHandlers.push(operation.onRefresh);
                        }

                        requestDefs.push(def);
                    });

                    if (storeUpdatePromises.length > 0) {
                        Promise.all(storeUpdatePromises).then(() => {
                            this.callOnRefresh(operation);
                        });
                    }
                    break;

                case 'delete':
                    _.each(operation.data, (data) => {
                        const oldAttributes = _.map(data.oldAttributes, (attributeName) => {
                            return this.getShortAttributeName(attributeName);
                        });

                        requests.push({
                            request: {
                                type: 'delete',
                                entity: data.entity,
                                dataSourceID: dataSourceID,
                                oldAttrValues: {
                                    attributes: oldAttributes,
                                    values: data.oldValues
                                }
                            }
                        });

                        // Records in backend's result go in the order of requests.
                        // So requestDefs must have the same order even if Def is not needed.
                        requestDefs.push(operation.defferedResult);
                    });

                    if (operation.onRefresh != null) {
                        onRefreshHandlers.push(operation.onRefresh);
                    }
                    break;

                default:
            }
        });

        if (requests.length === 0) {
            this.clearCollections(promiseKey);

            return result.resolve().promise();
        }

        this.set(promiseKey, result);

        this.callTransactionMethod(requests, runTimeWidgetID)
            .done((resp: any) => {
                const resultset = resp?.result?.resultset;

                if (resultset == null) {
                    this.glob.AppActions.showNotification_Fail('Result of a query to backend is null.');
                    this.rejectOperations(promiseKey, requestDefs, triggerCancelOnError);
                    result.reject();
                    return;
                }

                if (!Array.isArray(resultset) || resultset.length < requestDefs.length) {
                    this.glob.AppActions.showNotification_Fail(
                        'Wrong number of records in the result of a query to backend.'
                    );
                    this.rejectOperations(promiseKey, requestDefs, triggerCancelOnError);
                    result.reject();
                    return;
                }

                _.each(resultset, (obj, i) => {
                    const def = requestDefs[i];

                    if (def != null) {
                        def.resolve(obj);
                    }
                });

                _.each(onRefreshHandlers, (onRefresh) => {
                    onRefresh();
                });

                this.clearCollections(promiseKey, false);
                this.glob.EntityModelsActions.cleanCache(); //??
                this.trigger(ON_COMPLETE_OPERATIONS_EVENT);
                result.resolve();
            })
            .fail(() => {
                this.rejectOperations(promiseKey, requestDefs, triggerCancelOnError);
                result.reject();
            });

        return result.promise();
    }

    private rejectOperations(promiseKey: string, requestDefs: JQueryDeferred<any>[], triggerCancel: boolean) {
        _.each(requestDefs, (requestDef) => {
            if (requestDef != null) {
                requestDef.reject();
            }
        });

        this.cancelOperationsByPromiseKey(promiseKey, triggerCancel);
    }

    private callTransactionMethod(requests: ITransactionRequest[], runTimeWidgetID: number) {
        const keysMap = {};

        let virtualID = 1;

        requests.forEach(({ request }) => {
            const newAttrValues = request.newAttrValues;

            if (newAttrValues == null || newAttrValues.values == null) {
                return;
            }

            const { values, attributes } = newAttrValues;
            const tempKeyIndexes: number[] = [];

            values.forEach((value, index) => {
                if (isTemporaryKey(value)) {
                    tempKeyIndexes.unshift(index);
                }
            });

            if (tempKeyIndexes.length > 0) {
                const refAttributes: string[] = [];

                let refValues: any[] = [];

                tempKeyIndexes.forEach((keyIndex) => {
                    refAttributes.push(attributes.splice(keyIndex, 1)[0]);
                    refValues.push(values.splice(keyIndex, 1)[0]);
                });

                refValues = refValues.map((key) => {
                    if (keysMap[key] == null) {
                        keysMap[key] = virtualID;
                        virtualID += 1;
                    }

                    return keysMap[key];
                });

                newAttrValues.refAttributes = refAttributes;
                newAttrValues.refValues = refValues;
            }
        });

        let dataSourceID: number;

        const aqlOperations = requests.map(({ request, _tempKey }) => {
            const requestCopy = $.extend(true, {}, request);

            if (keysMap[_tempKey] != null) {
                requestCopy.virtualId = keysMap[_tempKey];
            }

            if (dataSourceID == null) {
                dataSourceID = requestCopy.dataSourceID;
            }

            delete requestCopy.dataSourceID;
            return requestCopy;
        });

        return this.glob.AQLQueries.callMethod(
            'attrQuery_Transaction',
            {
                dataSourceID: dataSourceID,
                operations: aqlOperations
            },
            null,
            null,
            {
                runTimeWidgetID: runTimeWidgetID,
                showInPopup: runTimeWidgetID === null
            }
        );
    }

    public addOnChangeListener(callback: Function) {
        this.on(ON_CHANGE_EVENT, callback);
    }

    public removeOnChangeListener(callback: Function) {
        this.off(ON_CHANGE_EVENT, callback);
    }

    public addOnCompleteOperationsListener(callback: Function) {
        this.on(ON_COMPLETE_OPERATIONS_EVENT, callback);
    }

    public removeOnCompleteOperationsListener(callback: Function) {
        this.off(ON_COMPLETE_OPERATIONS_EVENT, callback);
    }

    public addOnCancelOperationsListener(callback: Function) {
        this.on(ON_CANCEL_OPERATIONS_EVENT, callback);
    }

    public removeOnCancelOperationsListener(callback: Function) {
        this.off(ON_CANCEL_OPERATIONS_EVENT, callback);
    }
}
